import { DataSeries } from '@next/core-lib/models';

import {
  BaseDayRegistration,
  BaseFlock,
  BaseWeekRegistration,
  Breed,
  HenDeparture,
  HenHatchery,
  InputDefinition,
  SharedBarn,
} from './flock-shared.model';

export interface LayerFlock extends BaseFlock<LayerHenArrival, LayerWeekRegistration> {}

export interface LayerHenArrival {
  arrivalDate: string;
  breed: Breed;
  barn: SharedBarn;
  hatchery: HenHatchery;
  chickBirthDate: string;
  amountOfHens: number;
  eggMarketingStandard?: number;
  housingSystem?: number;
  deviationOfBirthDateInDays?: number;
  beddingType?: BeddingType;
  beddingAmount?: number;
  transportDistance?: number;
  timeInFreeRange?: number;
  henWeightInGrams?: number;
}

export enum BeddingType {
  NoBedding = 'NoBedding',
  BarleyStraw = 'BarleyStraw',
  MaizeStover = 'MaizeStover',
  OatStraw = 'OatStraw',
  SawDust = 'SawDust',
  SunflowerHusk = 'SunflowerHusk',
  WheatStraw = 'WheatStraw',
  Woodshavings = 'Woodshavings',
}

export interface LayerValueRegistration {
  mortality?: number;
  eggsAmount?: number;
  eggsAmountSecondKind?: number;
  eggWeight?: number;
  forage?: number;
  forageProduct?: string;
  isSecondForage?: boolean;
  secondForage?: number;
  secondForageProduct?: string;
  water?: number;
  henWeight?: number;
  isInMoult?: boolean;
  remarks?: string;
}

export interface LayerDayRegistration extends BaseDayRegistration<LayerValueRegistration> {}

export interface LayerWeekRegistration extends BaseWeekRegistration<LayerValueRegistration> {}

export enum InputMethodEnum {
  PerDay = 'PerDay',
  PerWeek = 'PerWeek',
}

export enum ValueRegistrationEnum {
  Mortality = 'mortality',
  EggsAmount = 'eggsAmount',
  EggsAmountSecondKind = 'eggsAmountSecondKind',
  EggWeight = 'eggWeight',
  Forage = 'forage',
  SecondForage = 'secondForage',
  ForageProduct = 'forageProduct',
  SecondForageProduct = 'secondForageProduct',
  Water = 'water',
  HenWeight = 'henWeight',
  IsInMoult = 'isInMoult',
}

export enum WeekRegistrationKPIEnum {
  AverageEggWeight = 'AverageEggWeight',
  AverageEggWeightCumulative = 'AverageEggWeightCumulative',
  AverageEggWeightPerLayerPerDay = 'AverageEggWeightPerLayerPerDay',
  AverageEggWeightPerLayerPerDayCumulative = 'AverageEggWeightPerLayerPerDayCumulative',
  AverageLayerWeight = 'AverageLayerWeight',
  EggCountPerLayerInProduction = 'EggCountPerLayerInProduction',
  EggCountPerLayerInProductionCumulative = 'EggCountPerLayerInProductionCumulative',
  FeedConsumption = 'FeedConsumption',
  SecondFeedConsumption = 'SecondFeedConsumption',
  FeedConsumptionPerLayerInProduction = 'FeedConsumptionPerLayerInProduction',
  FeedConsumptionPerLayerInProductionCumulative = 'FeedConsumptionPerLayerInProductionCumulative',
  FeedConsumptionPerLayerPerDay = 'FeedConsumptionPerLayerPerDay',
  FeedConsumptionPerLayerPerDayCumulative = 'FeedConsumptionPerLayerPerDayCumulative',
  FeedConversionRate = 'FeedConversionRate',
  FeedConversionRateCumulative = 'FeedConversionRateCumulative',
  FeedToEggRatio = 'FeedToEggRatio',
  FeedToEggRatioCumulative = 'FeedToEggRatioCumulative',
  MortalityCount = 'MortalityCount',
  MortalityRate = 'MortalityRate',
  MortalityRateCumulative = 'MortalityRateCumulative',
  RateOfLay = 'RateOfLay',
  RateOfLayCumulative = 'RateOfLayCumulative',
  TotalEggCount = 'TotalEggCount',
  TotalEggCountSecondGrade = 'TotalEggCountSecondGrade',
  TotalEggWeight = 'TotalEggWeight',
  TotalEggWeightPerLayerInProductionCumulative = 'TotalEggWeightPerLayerInProductionCumulative',
  WaterConsumption = 'WaterConsumption',
  WaterToFeedRatio = 'WaterToFeedRatio',
  WaterToFeedRatioCumulative = 'WaterToFeedRatioCumulative',
}

export interface WeekRegistrationKPIItem {
  name: string;
  translationKey?: string;
  value?: number;
  unit: string;
  decimalsPrecision?: number;
  showInOverview?: boolean;
  benchmark?: WeekRegistrationKPIItemBenchmark;
}

export interface WeekRegistrationKPIItemBenchmark {
  referenceKPI?: string;
  benchmarkCode?: string;
}

export interface WeekKPI {
  week?: number;
  weekStart?: Date;
  AverageEggWeight?: WeekRegistrationKPIItem;
  AverageEggWeightPerLayerPerDay?: WeekRegistrationKPIItem;
  AverageLayerWeight?: WeekRegistrationKPIItem;
  EggCountPerLayerInProduction?: WeekRegistrationKPIItem;
  EggCountPerLayerInProductionCumulative?: WeekRegistrationKPIItem;
  FeedConsumptionPerLayerPerDay?: WeekRegistrationKPIItem;
  FeedConsumptionPerLayerPerDayCumulative?: WeekRegistrationKPIItem;
  FeedConsumptionPerLayerInProduction?: WeekRegistrationKPIItem;
  FeedConsumptionPerLayerInProductionCumulative?: WeekRegistrationKPIItem;
  FeedConversionRate?: WeekRegistrationKPIItem;
  FeedConversionRateCumulative?: WeekRegistrationKPIItem;
  FeedToEggRatio?: WeekRegistrationKPIItem;
  MortalityRate?: WeekRegistrationKPIItem;
  MortalityRateCumulative?: WeekRegistrationKPIItem;
  RateOfLay?: WeekRegistrationKPIItem;
  TotalEggWeightPerLayerInProductionCumulative?: WeekRegistrationKPIItem;
  WaterToFeedRatio?: WeekRegistrationKPIItem;
  TotalEggWeight?: WeekRegistrationKPIItem;
  TotalEggCount?: WeekRegistrationKPIItem;
  TotalEggCountSecondGrade?: WeekRegistrationKPIItem;
  FeedConsumption?: WeekRegistrationKPIItem;
  SecondFeedConsumption?: WeekRegistrationKPIItem;
  MortalityCount?: WeekRegistrationKPIItem;
  WaterConsumption?: WeekRegistrationKPIItem;
}

export interface LayerFlockWeekDataSet {
  data: LayerFlockWeekData[];
  dataSeries: DataSeries<number, number>;
}

export interface LayerFlockWeekData {
  calculations: Record<string, CalculatedValue>;
  info: FlockWeekDataInfo;
  isInMoult: boolean;
  remarks: string[];
  values?: WeekDataValue[];
  week: number;
  weekStartDate: Date;
  weekEndDate: Date;
}

export interface WeekDataValue {
  parameter: string;
  value: number | null;
  unit?: string;
}

export interface AnimalCountCard {
  flockId: number;
  year: number;
  mutations: AnimalCountMutation[];
  totalNumberOfDays: number;
  totalAnimalDays: number;
  totalAnimalCount: number;
}

export interface AnimalCountMutation {
  date: Date;
  reason: string;
  mutation: number;
  amountBeforeMutation: number;
  amountAfterMutation: number;
  numberOfDays: number;
  animalDays: number;
}

export interface FlockWeekDataInfo {
  customerId: number;
  customerExternalId: string;
  customerName: string;
  customerAddressStreet: string;
  customerAddressHouseNr: string;
  customerAddressHouseNrAddition: string;
  customerAddressPostalCode: string;
  customerAddressCity: string;
  customerAddressCountry: string;
  locationId: number;
  locationExternalId: string;
  locationName: string;
  locationAddressStreet: string;
  locationAddressHouseNr: string;
  locationAddressHouseNrAddition: string;
  locationAddressPostalCode: string;
  locationAddressCity: string;
  locationAddressCountry: string;
  flockId: number;
  flockName: string;
  barnId: number;
  barnName: string;
  barnSurfaceArea: number;
  breedId: number;
  breedName: string;
  marketingStandard: number;
  dateOfBirth: Date;
  dateOfArrival: Date;
  dateOfProductionStart: Date;
  dateOfDeparture?: Date;
  hatcheryId: number;
  hatcheryName: string;
  layerCountStart: number;
  layerCountDeparture: number;
  ageStartProductionPeriod: number;
  layerCountStartProduction: number;
}

export interface CalculatedValue {
  parameter: string;
  value: number;
  dataType: string;
}

export type LayerFlockInputDefinition = InputDefinition;
export type LayerHenDeparture = HenDeparture;
